import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import {
  AlertCircle,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  Download,
  Hash,
  Send,
  XCircle,
} from 'lucide-react'
import { useId, useState } from 'react'
import { publisherApi } from '../lib/api'

interface PaymentRequest {
  id: string
  amount: number
  status: 'pending' | 'approved' | 'paid' | 'rejected'
  requestedAt: string
  processedAt?: string
  method: 'wire' | 'paypal' | 'crypto'
  notes?: string
  transactionId?: string
}

export function Payments() {
  const queryClient = useQueryClient()
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [requestAmount, setRequestAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<'wire' | 'paypal' | 'crypto'>('paypal')
  const [notes, setNotes] = useState('')

  // Generate unique IDs for form fields
  const amountId = useId()
  const paymentMethodId = useId()
  const notesId = useId()

  const { data: balance = { available: 0, pending: 0, lifetime: 0 } } = useQuery({
    queryKey: ['balance'],
    queryFn: publisherApi.getBalance,
  })

  const { data: paymentRequests = [] } = useQuery({
    queryKey: ['payment-requests'],
    queryFn: publisherApi.getPaymentRequests,
  })

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: publisherApi.getPaymentMethods,
  })

  const requestPaymentMutation = useMutation({
    mutationFn: (data: { amount: number; method: string; notes?: string }) =>
      publisherApi.requestPayment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-requests'] })
      queryClient.invalidateQueries({ queryKey: ['balance'] })
      setShowRequestModal(false)
      setRequestAmount('')
      setNotes('')
    },
  })

  const handleRequestPayment = () => {
    const amount = parseFloat(requestAmount)
    if (Number.isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount')
      return
    }
    if (amount > balance.available) {
      alert('Requested amount exceeds available balance')
      return
    }
    if (amount < 50) {
      alert('Minimum payout amount is $50')
      return
    }

    requestPaymentMutation.mutate({
      amount,
      method: paymentMethod,
      notes,
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-blue-500" />
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusBadge = (status: string) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    }
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'wire':
        return <Building className="w-4 h-4" />
      case 'paypal':
        return <CreditCard className="w-4 h-4" />
      case 'crypto':
        return <Hash className="w-4 h-4" />
      default:
        return <DollarSign className="w-4 h-4" />
    }
  }

  const currentMethod = paymentMethods.find((m) => m.type === paymentMethod)

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payments & Earnings</h1>
        <p className="text-gray-600 mt-1">Manage your earnings and request payments</p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Available Balance</p>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">${balance.available.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-1">Ready for withdrawal</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Pending Earnings</p>
            <Clock className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">${balance.pending.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-1">Awaiting approval</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Lifetime Earnings</p>
            <CheckCircle className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">${balance.lifetime.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-1">Total earned</p>
        </div>
      </div>

      {/* Request Payment Button */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Request Payment</h3>
            <p className="text-sm text-gray-600 mt-1">
              Minimum payout: $50 • Processing time: 3-5 business days
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowRequestModal(true)}
            disabled={balance.available < 50}
            className={clsx(
              'px-6 py-3 rounded-lg font-medium flex items-center transition-colors',
              balance.available >= 50
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            )}
          >
            <Send className="w-5 h-5 mr-2" />
            Request Payout
          </button>
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paymentRequests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm">No payment requests yet</p>
                  </td>
                </tr>
              ) : (
                paymentRequests.map((request: PaymentRequest) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        {format(new Date(request.requestedAt), 'MMM d, yyyy')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        ${request.amount.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-600">
                        {getMethodIcon(request.method)}
                        <span className="ml-2 capitalize">{request.method}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(request.status)}
                        <span
                          className={clsx(
                            'ml-2 px-2 py-1 text-xs font-medium rounded-full',
                            getStatusBadge(request.status),
                          )}
                        >
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {request.transactionId ? (
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {request.transactionId}
                        </code>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {request.status === 'paid' && (
                        <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-700 flex items-center"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Payment Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Payment</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor={amountId} className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (USD)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id={amountId}
                    type="number"
                    value={requestAmount}
                    onChange={(e) => setRequestAmount(e.target.value)}
                    placeholder="0.00"
                    min="50"
                    max={balance.available}
                    step="0.01"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Available: ${balance.available.toFixed(2)} • Min: $50.00
                </p>
              </div>

              <div>
                <label
                  htmlFor={paymentMethodId}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Payment Method
                </label>
                <select
                  id={paymentMethodId}
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as 'wire' | 'paypal' | 'crypto')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="paypal">PayPal</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="crypto">Cryptocurrency</option>
                </select>
              </div>

              {currentMethod && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Payment Details</p>
                  {Object.entries(currentMethod.details).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                      <span className="text-gray-900 font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label htmlFor={notesId} className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  id={notesId}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Add any special instructions..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowRequestModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRequestPayment}
                disabled={requestPaymentMutation.isPending}
                className={clsx(
                  'px-6 py-2 rounded-lg font-medium transition-colors',
                  requestPaymentMutation.isPending
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700',
                )}
              >
                {requestPaymentMutation.isPending ? 'Processing...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
